import React from "react";
import { Button, Input, Toggle } from "@teals/ui-web";
import type { FilterConfig, FilterParameter } from "../../store/api/dataSourcesApi";
import {
  BUILTIN_FILTER_SOURCES,
  FILTER_OPS,
  clauseValueFrom,
  customParamName,
  defaultClause,
  defaultOrGroupClause,
  defaultParameter,
  emptyFilterConfig,
  isEmptyFilterConfig,
  sourceSelectValue,
} from "./filterConfig";
import {
  FilterActions,
  FilterEditorHint,
  FilterRow,
  FilterSelect,
  FilterStack,
  FilterTableLabel,
  UnscopedWarning,
} from "./styles";

interface FilterConfigEditorProps {
  value: FilterConfig;
  onChange: (next: FilterConfig) => void;
  fieldOptions?: string[];
}

const FilterConfigEditor: React.FC<FilterConfigEditorProps> = ({
  value,
  onChange,
  fieldOptions = [],
}) => {
  const parameters = value.parameters ?? [];
  const clauses = value.clauses ?? [];

  const setParameters = (next: FilterParameter[]) =>
    onChange({ ...value, parameters: next });
  const setClauses = (next: FilterConfig["clauses"]) =>
    onChange({ ...value, clauses: next });

  const handleSourceChange = (index: number, selected: string) => {
    const current = parameters[index];
    if (selected === "custom") {
      const name = current.name && !BUILTIN_FILTER_SOURCES.some((s) => s.value === current.name)
        ? current.name
        : "custom_param";
      setParameters(
        parameters.map((p, i) =>
          i === index
            ? {
                ...p,
                source: `parameters.${name}`,
                name,
                label: p.label || name,
                required: p.required,
              }
            : p,
        ),
      );
      return;
    }
    const label =
      BUILTIN_FILTER_SOURCES.find((s) => s.value === selected)?.label || selected;
    setParameters(
      parameters.map((p, i) =>
        i === index
          ? { ...p, source: selected, name: selected, label }
          : p,
      ),
    );
  };

  return (
    <FilterStack>
      <FilterEditorHint>
        Bind request values (consumer organisation, consumer user, or extra
        parameters) to backend fields. Scoped filters are applied on every
        query and cannot be overridden by the agent. Leave empty for unscoped
        search.
      </FilterEditorHint>

      <FilterTableLabel>Parameters</FilterTableLabel>
      {parameters.map((param, index) => {
        const sourceKind = sourceSelectValue(param.source);
        return (
          <FilterRow key={`param-${index}`}>
            <FilterSelect
              aria-label={`Parameter ${index + 1} source`}
              value={sourceKind}
              onChange={(e) => handleSourceChange(index, e.target.value)}
            >
              {BUILTIN_FILTER_SOURCES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FilterSelect>
            {sourceKind === "custom" && (
              <Input
                value={customParamName(param.source) || param.name}
                onChange={(e) => {
                  const name = e.target.value.replace(/[^A-Za-z0-9_]/g, "");
                  setParameters(
                    parameters.map((p, i) =>
                      i === index
                        ? {
                            ...p,
                            name,
                            source: `parameters.${name}`,
                            label: name,
                          }
                        : p,
                    ),
                  );
                }}
                placeholder="parameter name"
              />
            )}
            <Toggle
              value={Boolean(param.required)}
              onChange={(next) =>
                setParameters(
                  parameters.map((p, i) =>
                    i === index ? { ...p, required: next } : p,
                  ),
                )
              }
              label="Required"
              size="sm"
            />
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() =>
                setParameters(parameters.filter((_, i) => i !== index))
              }
            >
              Remove
            </Button>
          </FilterRow>
        );
      })}
      <FilterActions>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => setParameters([...parameters, defaultParameter()])}
        >
          Add parameter
        </Button>
      </FilterActions>

      <FilterTableLabel>Filter clauses</FilterTableLabel>
      {clauses.map((clause, index) => {
        if (clause.op === "or_group") {
          const nested = clause.clauses ?? [];
          return (
            <FilterStack key={`clause-${index}`}>
              <FilterRow>
                <FilterEditorHint>OR group (any nested clause may match)</FilterEditorHint>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setClauses(clauses.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </FilterRow>
              {nested.map((child, nestedIndex) => {
                const bound = clauseValueFrom(child);
                const isStatic = !bound;
                return (
                  <FilterRow key={`clause-${index}-${nestedIndex}`}>
                    <Input
                      value={child.field || ""}
                      onChange={(e) =>
                        setClauses(
                          clauses.map((c, i) => {
                            if (i !== index) return c;
                            const nextNested = [...(c.clauses ?? [])];
                            nextNested[nestedIndex] = {
                              ...child,
                              field: e.target.value,
                            };
                            return { ...c, clauses: nextNested };
                          }),
                        )
                      }
                      placeholder="Backend field"
                      list={fieldOptions.length ? "ds-filter-fields" : undefined}
                    />
                    <FilterSelect
                      aria-label={`OR clause ${nestedIndex + 1} operator`}
                      value={child.op || "eq"}
                      onChange={(e) =>
                        setClauses(
                          clauses.map((c, i) => {
                            if (i !== index) return c;
                            const nextNested = [...(c.clauses ?? [])];
                            nextNested[nestedIndex] = {
                              ...child,
                              op: e.target.value,
                            };
                            return { ...c, clauses: nextNested };
                          }),
                        )
                      }
                    >
                      {FILTER_OPS.filter((op) => op.value !== "or_group").map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </FilterSelect>
                    <FilterSelect
                      aria-label={`OR clause ${nestedIndex + 1} binding`}
                      value={isStatic ? "__static__" : bound}
                      onChange={(e) => {
                        const next = e.target.value;
                        setClauses(
                          clauses.map((c, i) => {
                            if (i !== index) return c;
                            const nextNested = [...(c.clauses ?? [])];
                            nextNested[nestedIndex] =
                              next === "__static__"
                                ? { field: child.field, op: child.op, value: "" }
                                : {
                                    field: child.field,
                                    op: child.op,
                                    value_from: next,
                                  };
                            return { ...c, clauses: nextNested };
                          }),
                        );
                      }}
                    >
                      <option value="__static__">Static value</option>
                      {parameters.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.label || p.name}
                        </option>
                      ))}
                    </FilterSelect>
                    {isStatic && (
                      <Input
                        value={
                          Array.isArray(child.value)
                            ? child.value.join(", ")
                            : String(child.value ?? "")
                        }
                        onChange={(e) =>
                          setClauses(
                            clauses.map((c, i) => {
                              if (i !== index) return c;
                              const nextNested = [...(c.clauses ?? [])];
                              nextNested[nestedIndex] = {
                                ...child,
                                value: e.target.value,
                              };
                              return { ...c, clauses: nextNested };
                            }),
                          )
                        }
                        placeholder="Value"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() =>
                        setClauses(
                          clauses.map((c, i) =>
                            i === index
                              ? {
                                  ...c,
                                  clauses: (c.clauses ?? []).filter(
                                    (_, ni) => ni !== nestedIndex,
                                  ),
                                }
                              : c,
                          ),
                        )
                      }
                    >
                      Remove
                    </Button>
                  </FilterRow>
                );
              })}
              <FilterActions>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() =>
                    setClauses(
                      clauses.map((c, i) =>
                        i === index
                          ? {
                              ...c,
                              clauses: [
                                ...(c.clauses ?? []),
                                defaultClause(parameters[0]?.name),
                              ],
                            }
                          : c,
                      ),
                    )
                  }
                >
                  Add OR clause
                </Button>
              </FilterActions>
            </FilterStack>
          );
        }
        const bound = clauseValueFrom(clause);
        const isStatic = !bound;
        return (
          <FilterRow key={`clause-${index}`}>
            <Input
              value={clause.field || ""}
              onChange={(e) =>
                setClauses(
                  clauses.map((c, i) =>
                    i === index ? { ...c, field: e.target.value } : c,
                  ),
                )
              }
              placeholder="Backend field"
              list={fieldOptions.length ? "ds-filter-fields" : undefined}
            />
            <FilterSelect
              aria-label={`Clause ${index + 1} operator`}
              value={clause.op || "eq"}
              onChange={(e) =>
                setClauses(
                  clauses.map((c, i) => {
                    if (i !== index) return c;
                    if (e.target.value === "or_group") {
                      return defaultOrGroupClause();
                    }
                    return { ...c, op: e.target.value };
                  }),
                )
              }
            >
              {FILTER_OPS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              aria-label={`Clause ${index + 1} binding`}
              value={isStatic ? "__static__" : bound}
              onChange={(e) => {
                const next = e.target.value;
                setClauses(
                  clauses.map((c, i) => {
                    if (i !== index) return c;
                    if (next === "__static__") {
                      return { field: c.field, op: c.op, value: "" };
                    }
                    return {
                      field: c.field,
                      op: c.op,
                      value_from: next,
                    };
                  }),
                );
              }}
            >
              <option value="__static__">Static value</option>
              {parameters.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.label || p.name}
                </option>
              ))}
            </FilterSelect>
            {isStatic && (
              <Input
                value={
                  Array.isArray(clause.value)
                    ? clause.value.join(", ")
                    : String(clause.value ?? "")
                }
                onChange={(e) =>
                  setClauses(
                    clauses.map((c, i) =>
                      i === index ? { ...c, value: e.target.value } : c,
                    ),
                  )
                }
                placeholder="Value"
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setClauses(clauses.filter((_, i) => i !== index))}
            >
              Remove
            </Button>
          </FilterRow>
        );
      })}
      <FilterActions>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() =>
            setClauses([
              ...(clauses ?? []),
              defaultClause(parameters[0]?.name),
            ])
          }
        >
          Add clause
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => setClauses([...(clauses ?? []), defaultOrGroupClause()])}
        >
          Add OR group
        </Button>
      </FilterActions>
      {fieldOptions.length > 0 && (
        <datalist id="ds-filter-fields">
          {fieldOptions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      )}
      {isEmptyFilterConfig(value) && (
        <UnscopedWarning>
          No query filters — searches are unscoped. Mixed-corpus indexes
          (drafts, private records) must not stay unscoped.
        </UnscopedWarning>
      )}
    </FilterStack>
  );
};

export { emptyFilterConfig };
export default FilterConfigEditor;
